/**
 * "What is running", answered in one line above the table it summarises.
 *
 * On Обзор the full book is the last block, which is right — nineteen rows and
 * twelve columns must not push every other region below the fold. But "what is
 * running" is also the first question an operator has, and a page that makes
 * them scroll past everything to reach it has answered late. So it is answered
 * twice there: a line at the top, the book at the bottom, and a link between.
 *
 * Циклы shows the same line without the link, because on that screen the table
 * is already the next thing on the page. Shared rather than written twice: two
 * copies of "how many are losing" would eventually disagree, and the screen
 * that disagreed would be the one somebody was reading.
 *
 * Nothing in this line is computed from money — it counts rows and compares
 * timestamps, which is the one kind of arithmetic a screen may do because
 * there is no ledger figure to disagree with.
 */
import { ArrowDown } from "lucide-react";
import { Button } from "@/design-system/Button";

import type { SlotResponse } from "@/shared/api";
import { isNegative, isZero, toPlaces } from "@/shared/money/money";
import { generationOfVersion, versionOf } from "@/shared/strategy/versions";
import { humanAge } from "@/shared/time/age";

const POOLS = ["PRIME", "PULSE", "FLASH"] as const;

/** How long a cycle has to be open before it is worth counting separately. */
const AGEING_HOURS = 48;

/** How long every open cycle has been open, oldest first. */
function ages(slots: readonly SlotResponse[], now: number): readonly number[] {
  return slots
    .filter((slot) => slot.opened_at !== null)
    .map((slot) => now - new Date(slot.opened_at ?? "").getTime())
    .filter((age) => Number.isFinite(age) && age >= 0)
    .sort((left, right) => right - left);
}

/**
 * How deep the ageing goes, for the count that names it.
 *
 * "Старше 48ч 26" reads the same whether the median cycle is two days old or
 * five, and those are a book with a few stragglers and a strategy that has
 * stopped closing. The count stays the headline — it is what the strip is for
 * — and the depth sits on it, where a reader who wants it can reach it without
 * two more figures appearing on a line that already carries seven.
 */
function depth(ageing: readonly number[]): string {
  if (ageing.length === 0) {
    return `Циклов старше ${String(AGEING_HOURS)} часов нет.`;
  }
  const middle = Math.floor(ageing.length / 2);
  const median =
    ageing.length % 2 === 1
      ? (ageing[middle] ?? 0)
      : ((ageing[middle - 1] ?? 0) + (ageing[middle] ?? 0)) / 2;
  const oldest = ageing[0] ?? 0;
  return `Из них: медиана ${humanAge(median / 1000)} · старейшему ${humanAge(
    oldest / 1000,
  )}.`;
}

export function LiveCycleSummary({
  slots,
  onJump,
  total = true,
  freeSlots,
  activeCycles,
}: {
  readonly slots: readonly SlotResponse[];
  readonly activeCycles?: number | undefined;
  /** Omitted where the table is already the next block on the page. */
  readonly onJump?: (() => void) | undefined;
  /**
   * Whether the line carries the total itself. False where the table below it
   * already states its own size on its heading — §8.2 keeps the count with the
   * collection's label, and the same number twice on one screen is the defect
   * §8.6 names, not thoroughness.
   */
  readonly total?: boolean;
  /**
   * Slots the schedule allows that no cycle occupies. Optional because it
   * comes from the capital read model rather than from the roster, and a
   * screen that does not have it says nothing rather than computing one.
   */
  readonly freeSlots?: number | undefined;
}): React.JSX.Element | null {
  if (slots.length === 0) return null;

  // Which pool each running cycle trades in: the owner's own vocabulary for
  // the book. A cycle of the retired BASE v1.3 is still counted on its own
  // while one is open.
  const pools = POOLS.map((pool) => ({
    pool,
    count: slots.filter((slot) => slot.logical_pool === pool).length,
  })).filter((item) => item.count > 0);
  const previous = slots.filter((slot) => {
    const version = versionOf(slot);
    return version !== null && generationOfVersion(version) === "v1";
  }).length;

  const results = slots.map((slot) => toPlaces(slot.unrealised));
  const losing = results.filter((value) => isNegative(value)).length;
  const winning = results.filter((value) => !isNegative(value) && !isZero(value)).length;
  const now = Date.now();
  // Only the cycles past the threshold: a median taken over the whole book
  // would describe a different set from the one the count names, and two
  // figures beside each other about different sets is worse than one.
  const ageing = ages(slots, now).filter(
    (age) => age > AGEING_HOURS * 3600 * 1000,
  );

  // Counts, and nothing but counts. They used to be painted in the result
  // scale — seven red, twelve green, nineteen amber — and §7 reserves those
  // colours for a result, which carries a sign and a glyph beside it. A count
  // has neither: "7" in red is not a loss, it is how many cycles are currently
  // below water, and the word beside it already says so. Three result colours
  // on a strip of seven pairs also left nothing for the reader's eye to rank.
  const facts: readonly {
    readonly label: string;
    readonly value: string;
    readonly title?: string;
  }[] = [
    ...(total ? [{ label: "Активно", value: String(activeCycles ?? slots.length) }] : []),
    ...pools.map((item) => ({ label: item.pool, value: String(item.count) })),
    // Only while a cycle of the retired strategy is still open.
    ...(previous > 0 ? [{ label: "BASE v1.3", value: String(previous) }] : []),
    ...(freeSlots === undefined
      ? []
      : [{ label: "Свободных слотов", value: String(freeSlots) }]),
    { label: "В минусе", value: String(losing) },
    { label: "В плюсе", value: String(winning) },
    {
      label: `Старше ${String(AGEING_HOURS)}ч`,
      value: String(ageing.length),
      title: depth(ageing),
    },
  ];

  return (
    <section
      data-testid="live-cycle-summary"
      aria-label="Активные циклы, сводка"
      className="flex min-h-8 flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-panel border border-border-subtle bg-surface-default px-4 py-2"
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
        {facts.map((fact) => (
          <span
            key={fact.label}
            title={fact.title}
            className="flex items-baseline gap-1.5"
          >
            <span className="text-helper-sm text-text-muted">{fact.label}</span>
            <span
              data-testid={`summary-${fact.label}`}
              className="text-body-main tabular-nums text-text-primary"
            >
              {fact.value}
            </span>
          </span>
        ))}
        {/* A count of "critical" cycles is deliberately absent: no definition
            of critical exists in the system, and a zero beside the others
            would read as a measurement of something nobody has defined. */}
      </div>

      {onJump !== undefined && (
      <Button
        variant="ghost"
        size="sm"
        onClick={onJump}
        data-testid="jump-to-cycles"
        className="gap-1 text-action-primary hover:text-action-hover"
      >
        Перейти к активным циклам
        <ArrowDown aria-hidden="true" className="size-3.5 shrink-0" />
      </Button>
      )}
    </section>
  );
}
